import { Component } from "react"

/* Constants */
import { roads } from "../constants"


/* TYPES */
const BEAD_PLATE = "bead-plate"
const BIG_ROAD = "big-road"
const BIG_EYE = "big-eye"
const SMALL_ROAD = "small-road"
const COCKROACH_ROAD = "cockroach-road"

/* Derived Road Offsets */
const OFFSETS = { [BIG_EYE]: 1, [SMALL_ROAD]: 2, [COCKROACH_ROAD]: 3 }

/* Distance in pixels that turns a tap into a scroll gesture */
const DRAG_LIMIT = 6

/* Widget Roads */
class Roads extends Component {

    constructor() {
        super()

        this.state = {
            selected: ""
        }

        this._roads = {}
        this._origin = null
        this._dragged = false
        this._timer = null
    }

    componentDidMount = () => {
        this.scrollAll()
    }

    componentWillUnmount = () => {
        if (this._timer) clearTimeout(this._timer)
    }

    /* Scroll to end */
    scrollAll = () => {

        /* Start Cycle */
        for (const key in this._roads) {

            /* Check Road */
            if (!this._roads[key]) continue

            /* Get Road */
            const road = this._roads[key]

            /* Bead Plate | Nothing to scroll while the results fit into the visible columns */
            if (key === BEAD_PLATE && this.props.results.length <= 60) {
                road.scrollLeft = 0
                continue
            }

            /* Scroll to end */
            road.scrollLeft = road.scrollWidth
        }

    }


    /* Anchor The Road To Its Last Column */
    anchor = type => {

        /* Check Road */
        if (!this._roads[type]) {
            return
        }

        /* Get Road */
        const road = this._roads[type]

        /* Scroll to end */
        road.scrollLeft = road.scrollWidth

    }

    /* Remember Where The Gesture Started */
    press = event => {
        this._origin = { x: event.clientX, y: event.clientY }
        this._dragged = false
    }

    /* Detect A Scroll Gesture | A drag must not toggle the road */
    move = event => {

        /* Check Gesture | Only a pressed pointer counts, a hover must be ignored */
        if (!this._origin || this._dragged || event.buttons === 0) {
            return
        }

        /* Measure Distance */
        const dx = Math.abs(event.clientX - this._origin.x)
        const dy = Math.abs(event.clientY - this._origin.y)

        if (dx > DRAG_LIMIT || dy > DRAG_LIMIT) {
            this._dragged = true
        }

    }

    /* Select */
    select = type => {

        /* Fields */
        const { selected } = this.state
        const dragged = this._dragged

        /* Close The Gesture */
        this._origin = null
        this._dragged = false

        /* Skip The Scroll Gesture */
        if (dragged) {
            return
        }

        /* Toggle Road */
        this.setState({ selected: type === selected ? "" : type }, () => {

            /* Keep the last column in sight right away */
            this.anchor(type)

            /* The box is still resizing | Anchor once more when the transition is over */
            if (this._timer) clearTimeout(this._timer)
            this._timer = setTimeout(() => this.anchor(type), 450)

        })

    }

    /* Road Handlers */
    handlers = type => {
        return {
            onPointerDown: this.press,
            onPointerMove: this.move,
            onClick: () => this.select(type),
        }
    }

    /* Generate Grid */
    generateGrid = type => {

        /* Fields */
        const { results: _results } = this.props

        /* Get Results */
        const results = this.prepare(_results)

        /* Big Road */
        if (type === BIG_ROAD) {
            return roads.buildBigRoad(results)
        }

        /* Derived Roads | Big Eye 1, Small Road 2, Cockroach Road 3 */
        if (OFFSETS[type]) {
            const sequence = roads.buildBigRoadLogicalSequence(results)
            const signals = roads.buildDerivedSignals(sequence, OFFSETS[type])
            return roads.buildRoadFromSignals(signals)
        }

        /* Unknown Road */
        return Array.from({ length: 6 }, () => [])
    }

    /* Predict The Derived Marks Of The Next Result
       Returns the mark every derived road would get if the next hand was won by the candidate */
    predict = candidate => {

        /* Fields */
        const { results } = this.props

        /* Build Both Sequences */
        const current = this.prepare(results)
        const next = [...current, { result: candidate, playerPair: false, bankerPair: false }]

        const currentSeq = roads.buildBigRoadLogicalSequence(current)
        const nextSeq = roads.buildBigRoadLogicalSequence(next)

        /* Marks Field */
        const marks = {}

        /* Start Cycle */
        for (const type in OFFSETS) {

            const before = roads.buildDerivedSignals(currentSeq, OFFSETS[type])
            const after = roads.buildDerivedSignals(nextSeq, OFFSETS[type])

            /* The road is too short to say anything yet */
            marks[type] = after.length > before.length ? after[after.length - 1] : ""
        }

        return marks
    }

    /* Prepare Results */
    prepare = data => {
        return data.map(x => {

            /* Determine Result */
            let result = null
            if (x.win === "player") result = "P"
            if (x.win === "banker") result = "B"
            if (x.win === "tie") result = "T"

            /* Check Result */
            if (!result) return null

            return { result, playerPair: x.playerPair === true, bankerPair: x.bankerPair === true }
        })
    }

    /* Draw Pair Dots */
    _pairs = (prefix, value) => {
        return (
            <>
                {value.playerPair ? <div className={`${prefix} player`} /> : null}
                {value.bankerPair ? <div className={`${prefix} banker`} /> : null}
            </>
        )
    }

    /* Draw Cell */
    _cell = (type, value, key) => {

        /* Check Cell */
        if (!value) return <div className="road-cell empty" key={`${key}`} />

        /* Determine Style */
        let cellStyle = ""

        /* Big Road */
        if (type === BIG_ROAD) {
            if (value.result === "T") cellStyle = "road-disk tie"
            else if (value.result === "B") cellStyle = "road-disk banker"
            else cellStyle = "road-disk player"
        }

        /* Big Eye */
        if (type === BIG_EYE) {
            cellStyle = value.v === "R" ? "road-circle red" : "road-circle blue"
        }

        /* Small Road */
        if (type === SMALL_ROAD) {
            cellStyle = value.v === "R" ? "road-dot red" : "road-dot blue"
        }

        /* Cockroach Road */
        if (type === COCKROACH_ROAD) {
            cellStyle = value.v === "R" ? "road-line red" : "road-line blue"
        }

        return (
            <div className="road-cell" key={`${key}`}>
                <div className={cellStyle}>
                    {type === BIG_ROAD ? this._pairs("road-pair", value) : null}
                    {type === BIG_ROAD && value.ties > 0 ? <div className="road-tie">{value.ties > 1 ? <div className="road-tie-number">{value.ties}</div> : null}</div> : null}
                </div>
            </div>
        )
    }

    /* Draw Plate */
    _plate = (value, key) => {

        /* Check Plate */
        if (!value) return <div className="bead-plate-cell" key={`${key}`} />

        /* Determine Style */
        let cellStyle = "bead-plate-cell"
        if (value.result === "P") cellStyle = `${cellStyle} player`
        if (value.result === "B") cellStyle = `${cellStyle} banker`
        if (value.result === "T") cellStyle = `${cellStyle} tie`

        return (
            <div className={cellStyle} key={`${key}`}>
                {this._pairs("bead-plate-pair", value)}
                {value.result}
            </div>
        )
    }


    /* Draw Road */
    drawRoad = (type = "") => {

        /* Generate Grid */
        const grid = this.generateGrid(type)
        const roadWidth = Math.max(...grid.map(row => row.length))

        return (
            <div className="road-scale-wrapper" key={`${type}`} style={{ "--cols": `${roadWidth}` }}>
                <div className="road-grid">
                    {grid.map((row, r) =>
                        <div className="road-row" key={`${r}`}>
                            {row.map((cell, c) => this._cell(type, cell, `${r}-${c}`))}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    /* Draw Predictor */
    _predictor = () => {

        /* Fields */
        const list = [{ result: "P", style: "player" }, { result: "B", style: "banker" }]

        /* Mark Color | R means red, B means blue */
        const color = mark => {
            if (mark === "R") return "red"
            if (mark === "B") return "blue"
            return "none"
        }

        return (
            <div className="road-predictor">

                {list.map(item => {

                    /* Get Marks */
                    const marks = this.predict(item.result)

                    return (
                        <div className={`road-predict ${item.style}`} key={`${item.result}`}>

                            {/* Candidate */}
                            <div className="road-predict-plate">{item.result}</div>

                            {/* Big Eye */}
                            <div className={`road-predict-circle ${color(marks[BIG_EYE])}`} />

                            {/* Small Road */}
                            <div className={`road-predict-dot ${color(marks[SMALL_ROAD])}`} />

                            {/* Cockroach Road */}
                            <div className={`road-predict-line ${color(marks[COCKROACH_ROAD])}`} />

                        </div>
                    )
                })}

            </div>
        )
    }

    /* Draw Numbers */
    _numbers = () => {

        /* Fields */
        const { results } = this.props

        return (
            <div className="road-numbers">

                <div className="road-counters">

                    {/* Total */}
                    <div className="road-number">#{results.length}</div>

                    {/* Player */}
                    <div className="road-number-box">
                        <div className="road-number-plate player">P</div>
                        <div className="road-number-value">{results.filter(e => e.win === "player").length}</div>
                    </div>

                    {/* Banker */}
                    <div className="road-number-box">
                        <div className="road-number-plate banker">B</div>
                        <div className="road-number-value">{results.filter(e => e.win === "banker").length}</div>
                    </div>

                    {/* Tie */}
                    <div className="road-number-box">
                        <div className="road-number-plate tie">T</div>
                        <div className="road-number-value">{results.filter(e => e.win === "tie").length}</div>
                    </div>

                    {/* Player Pair */}
                    <div className="road-number-box">
                        <div className="road-number-pair player" />
                        <div className="road-number-value">{results.filter(e => e.playerPair === true).length}</div>
                    </div>

                    {/* Banker Pair */}
                    <div className="road-number-box">
                        <div className="road-number-pair banker" />
                        <div className="road-number-value">{results.filter(e => e.bankerPair === true).length}</div>
                    </div>

                </div>

                {/* Draw Predictor */}
                {this._predictor()}

            </div>
        )
    }


    render = () => {

        /* Fields */
        const { type, results: _results } = this.props
        const { selected } = this.state

        /* Draw Bead Plate */
        if (type === BEAD_PLATE) {

            /* Get Results */
            const results = this.prepare(_results)

            /* Build Grid */
            const grid = roads.buildBeadPlate(results, 6, 18)

            return (
                <div ref={ref => this._roads[BEAD_PLATE] = ref} className="bead-plate">
                    {grid.map((row, r) =>
                        <div className="bead-plate-row" key={`${r}`}>
                            {row.map((plate, c) => this._plate(plate, `${r}-${c}`))}
                        </div>
                    )}
                </div>
            )
        }

        return (
            <div className="roads">

                {/* Draw Numbers */}
                {this._numbers()}

                <div className="roads-container">

                    {/* Draw Big Road */}
                    <div ref={ref => this._roads[BIG_ROAD] = ref} {...this.handlers(BIG_ROAD)} className={`road-box big-road ${selected === BIG_ROAD ? "selected" : "default"}`}>
                        {this.drawRoad(BIG_ROAD)}
                    </div>

                    {/* Draw Big Eye */}
                    <div ref={ref => this._roads[BIG_EYE] = ref} {...this.handlers(BIG_EYE)} className={`road-box big-eye ${selected === BIG_EYE ? "selected" : "default"}`}>
                        {this.drawRoad(BIG_EYE)}
                    </div>

                    {/* Draw Small Road */}
                    <div ref={ref => this._roads[SMALL_ROAD] = ref} {...this.handlers(SMALL_ROAD)} className={`road-box small-road ${selected === SMALL_ROAD ? "selected" : "default"}`}>
                        {this.drawRoad(SMALL_ROAD)}
                    </div>

                    {/* Cockroach Small Road */}
                    <div ref={ref => this._roads[COCKROACH_ROAD] = ref} {...this.handlers(COCKROACH_ROAD)} className={`road-box cockroach-road ${selected === COCKROACH_ROAD ? "selected" : "default"}`}>
                        {this.drawRoad(COCKROACH_ROAD)}
                    </div>

                </div>

            </div>
        )
    }

}

export default Roads